import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { Award, ArrowLeft, Loader2, Info } from "lucide-react";
import {
  KarakterSelect, MoedestabilitetSelect, DatoField, UnderviserSelect,
  beregnGennemsnit, KarakterBadge
} from "../components/shared/GradeHelpers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AfsluttendeEksamenPage() {
  const { appItemId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [structure, setStructure] = useState({ praktiske_fag: [], teoretiske_fag: [] });
  const [data, setData] = useState({});
  const [undervisere, setUndervisere] = useState([]);
  const [akkreditering, setAkkreditering] = useState({});
  const [uddannelsestype, setUddannelsestype] = useState("kos");
  const [saving, setSaving] = useState(false);

  const adminToken = user?.token;

  useEffect(() => {
    if (!appItemId || !adminToken) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/elev/stamdata/${appItemId}/afsluttende-eksamen-v2`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        setStructure(res.data.structure || { praktiske_fag: [], teoretiske_fag: [] });
        setData(res.data.data || {});
        setUndervisere(res.data.undervisere || []);
        setAkkreditering(res.data.akkreditering || {});
        setUddannelsestype(res.data.uddannelsestype || "kos");
      } catch (e) {
        console.error("Kunne ikke hente afsluttende eksamen:", e);
      }
      setLoading(false);
    })();
  }, [appItemId, adminToken]);

  const save = useCallback(async (updates) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/elev/stamdata/${appItemId}/afsluttende-eksamen-v2`, updates, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
    } catch (e) {
      console.error("Gem fejl:", e);
    }
    setSaving(false);
  }, [appItemId, adminToken]);

  const updateMeta = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
    save({ [field]: value });
  };

  const updateFag = (section, fagKey, fagData) => {
    setData(prev => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [fagKey]: fagData },
    }));
    save({ [section]: { [fagKey]: fagData } });
  };

  // Beregn gennemsnit
  const allGrades = [];
  for (const section of ["praktiske_fag", "teoretiske_fag"]) {
    const fags = data[section] || {};
    for (const vals of Object.values(fags)) {
      if (vals.eksamenskarakter) allGrades.push(vals.eksamenskarakter);
      else if (vals.aarskarakter) allGrades.push(vals.aarskarakter);
    }
  }
  const autoGennemsnit = beregnGennemsnit(allGrades);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button onClick={() => window.close()} className="p-2 rounded-lg hover:bg-secondary transition-colors">
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            <h1 className="text-lg font-bold text-foreground">Afsluttende eksamen</h1>
            <span className="text-[13px] text-muted-foreground bg-secondary px-2 py-0.5 rounded-full uppercase">
              {uddannelsestype === "kos" ? "Kosmetolog" : "Frisør"}
            </span>
          </div>
          {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground ml-auto" />}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Meta: dato, censor, underviser */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-[13px] text-muted-foreground font-medium block mb-1.5">Dato</label>
              <DatoField value={data.dato || ""} onChange={v => updateMeta("dato", v)} className="w-full" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground font-medium block mb-1.5">Censor</label>
              <input
                type="text"
                value={data.censor || ""}
                onChange={e => setData(prev => ({ ...prev, censor: e.target.value }))}
                onBlur={e => save({ censor: e.target.value })}
                placeholder="Censornavn"
                className="bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground w-full focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground font-medium block mb-1.5">Underviser</label>
              <UnderviserSelect
                value={data.underviser || ""}
                onChange={v => updateMeta("underviser", v)}
                undervisere={undervisere}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Praktiske fag */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-bold text-foreground uppercase tracking-wider">Praktiske fag</h2>
          </div>
          {/* Header */}
          <div className="hidden sm:grid sm:grid-cols-[1fr_100px_100px] gap-2 px-5 py-2 bg-secondary/50 text-[13px] text-muted-foreground font-medium">
            <span>Fag</span>
            <span className="text-center">Årskarakter</span>
            <span className="text-center">Eksamen</span>
          </div>
          <div className="divide-y divide-border">
            {structure.praktiske_fag.map(fag => (
              <FagRow
                key={fag.key}
                fagKey={fag.key}
                label={fag.label}
                data={(data.praktiske_fag || {})[fag.key]}
                onChange={(k, v) => updateFag("praktiske_fag", k, v)}
              />
            ))}
          </div>
        </div>

        {/* Teoretiske fag */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-sm font-bold text-foreground uppercase tracking-wider">Teoretiske fag</h2>
          </div>
          <div className="hidden sm:grid sm:grid-cols-[1fr_100px_100px] gap-2 px-5 py-2 bg-secondary/50 text-[13px] text-muted-foreground font-medium">
            <span>Fag</span>
            <span className="text-center">Årskarakter</span>
            <span className="text-center">Eksamen</span>
          </div>
          <div className="divide-y divide-border">
            {structure.teoretiske_fag.map(fag => (
              <FagRow
                key={fag.key}
                fagKey={fag.key}
                label={fag.label}
                data={(data.teoretiske_fag || {})[fag.key]}
                onChange={(k, v) => updateFag("teoretiske_fag", k, v)}
              />
            ))}
          </div>
        </div>

        {/* Samlet vurdering */}
        <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wider">Samlet vurdering</h2>

          <div className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-border">
            <span className="text-sm text-foreground font-medium">Gennemsnit</span>
            <div className="flex items-center gap-2">
              {autoGennemsnit && <span className="text-[13px] text-muted-foreground">Beregnet: {autoGennemsnit}</span>}
              <input
                type="text"
                value={data.gennemsnit || ""}
                onChange={e => setData(prev => ({ ...prev, gennemsnit: e.target.value }))}
                onBlur={e => save({ gennemsnit: e.target.value })}
                placeholder={autoGennemsnit || "—"}
                className="bg-secondary border border-border rounded-md px-2 py-1.5 text-sm text-foreground w-20 text-center focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {uddannelsestype === "kos" && (
            <div className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-border">
              <span className="text-sm text-foreground font-medium">Helhed</span>
              <KarakterSelect value={data.helhed || ""} onChange={v => updateMeta("helhed", v)} className="w-[100px]" />
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-border">
            <span className="text-sm text-foreground font-medium">Mødestabilitet</span>
            <MoedestabilitetSelect value={data.moedestabilitet || ""} onChange={v => updateMeta("moedestabilitet", v)} className="w-[220px]" />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-border">
            <span className="text-sm text-foreground font-medium">Uddannelsens omfang</span>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min="0"
                value={data.uddannelsens_omfang_timer ?? ""}
                onChange={e => setData(prev => ({ ...prev, uddannelsens_omfang_timer: e.target.value === "" ? null : parseFloat(e.target.value) }))}
                onBlur={e => save({ uddannelsens_omfang_timer: e.target.value === "" ? null : parseFloat(e.target.value) })}
                placeholder="—"
                className="bg-secondary border border-border rounded-md px-2 py-1.5 text-sm text-foreground w-24 text-right focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <span className="text-[13px] text-muted-foreground">timer</span>
            </div>
          </div>

          <div className="py-2">
            <label className="text-sm text-foreground font-medium block mb-1.5">Noter</label>
            <textarea
              value={data.noter || ""}
              onChange={e => setData(prev => ({ ...prev, noter: e.target.value }))}
              onBlur={e => save({ noter: e.target.value })}
              rows={3}
              placeholder="Eventuelle noter..."
              className="bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground w-full focus:outline-none focus:ring-1 focus:ring-primary resize-y"
            />
          </div>
        </div>

        {/* Akkrediteringstekst */}
        {akkreditering.tekst && (
          <div className="bg-secondary/50 border border-border rounded-2xl px-5 py-4">
            <div className="flex items-start gap-2.5">
              <Info className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              <p className="text-[13px] text-muted-foreground italic whitespace-pre-line leading-relaxed">
                {akkreditering.tekst}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Fag-række med årskarakter + eksamenskarakter ─── */
function FagRow({ fagKey, label, data, onChange }) {
  const saved = data || {};
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px_100px] gap-2 items-center px-5 py-2.5">
      <span className="text-sm text-foreground">{label}</span>
      <div className="sm:hidden flex gap-2 items-center">
        <span className="text-[13px] text-muted-foreground w-16">Års:</span>
        <KarakterSelect value={saved.aarskarakter || ""} onChange={v => onChange(fagKey, { ...saved, aarskarakter: v })} className="flex-1" />
      </div>
      <div className="sm:hidden flex gap-2 items-center">
        <span className="text-[13px] text-muted-foreground w-16">Eksamen:</span>
        <KarakterSelect value={saved.eksamenskarakter || ""} onChange={v => onChange(fagKey, { ...saved, eksamenskarakter: v })} className="flex-1" />
      </div>
      <div className="hidden sm:block">
        <KarakterSelect value={saved.aarskarakter || ""} onChange={v => onChange(fagKey, { ...saved, aarskarakter: v })} className="w-full" />
      </div>
      <div className="hidden sm:block">
        <KarakterSelect value={saved.eksamenskarakter || ""} onChange={v => onChange(fagKey, { ...saved, eksamenskarakter: v })} className="w-full" />
      </div>
    </div>
  );
}
